import { groupColumns } from '../lib/pumsemTableGroup';
import { LABEL_CELL, spanLayout } from '../lib/pumsemTableSpan';
import type { PumsemTable as PumsemTableModel } from '../model/pumsem.type';
import { PumsemChangeBadge } from './PumsemChangeBadge';
import {
  CHANGED_CELL,
  SPANNED_CELL,
  TABLE_CELL,
  TABLE_HEAD,
  TABLE_ROW_LABEL,
} from './pumsemStyles';

interface PumsemTableProps {
  table: PumsemTableModel;
  /** 행 배지에 붙일 판 표기(`'26상`). 없으면 배지에 판을 안 적는다. */
  editionLabel?: string;
  /** 개정 표시를 끈다. 비교 화면의 '이전 판' 쪽처럼 표기가 무의미한 자리. */
  hideChanges?: boolean;
  actions?: React.ReactNode;
}

/**
 * 품 표.
 *
 * ── 절대 하면 안 되는 것 ────────────────────────────────────────
 * 값을 숫자로 바꾸지 않는다. 표준품셈은 유효자릿수 자체가 규정이라 `0.010` 을
 * `0.01` 로 그리면 다른 값이 되고, `1,560` 처럼 쉼표가 박힌 값과 `-`·`별도계상`
 * 같은 비수치 값도 그대로 온다.
 *
 * 표는 **자기 안에서 가로 스크롤**한다. 규격이 열이라 폭이 쉽게 넘치는데,
 * 페이지를 가로로 흘리면 좌측 결과 목록까지 같이 밀린다.
 *
 * 기준 단위(`팀당`)는 발행본과 같이 **표 우측 상단**에 둔다 — 실무자가 그 자리를
 * 보고 "무엇 하나당"을 읽는다.
 */
export function PumsemTable({
  table,
  editionLabel,
  hideChanges = false,
  actions,
}: PumsemTableProps) {
  const groups = groupColumns(table.columns);
  const layout = spanLayout(table);
  const hasGroupRow = groups.some((group) => group.label !== null);
  const rowHeader = table.rowHeader?.trim() || '구분';
  const columnHeader = table.columnHeader?.trim();
  const axisHeader = columnHeader ? (
    <span className="relative block min-h-10 min-w-24 overflow-hidden">
      <span className="absolute inset-0 bg-[linear-gradient(to_top_right,transparent_49%,currentColor_50%,transparent_51%)] opacity-30" />
      <span className="absolute right-0 top-0">{columnHeader}</span>
      <span className="absolute bottom-0 left-0">{rowHeader}</span>
    </span>
  ) : rowHeader;

  return (
    <figure className="my-5 overflow-hidden rounded-xl border border-neutral-200 dark:border-white/12">
      <div className="flex items-center gap-2 border-b border-neutral-200 bg-neutral-50 px-3.5 py-2.5 dark:border-white/12 dark:bg-white/[0.03]">
        <figcaption className="text-[14px] font-bold text-neutral-800 dark:text-neutral-100">
          {table.caption || '품'}
        </figcaption>
        {actions && <div className="ml-auto flex gap-1.5">{actions}</div>}
        {table.valueUnit && (
          <span
            className={`text-[13px] text-neutral-500 dark:text-neutral-400 ${actions ? '' : 'ml-auto'}`}
          >
            ({table.valueUnit})
          </span>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[14.5px]">
          <thead>
            {hasGroupRow && (
              <tr className="border-b border-neutral-200 dark:border-white/12">
                <th className={`${TABLE_HEAD} text-left`} rowSpan={2}>
                  {axisHeader}
                </th>
                {groups.map((group) =>
                  group.label ? (
                    <th
                      key={`g:${group.label}:${group.columns[0].key}`}
                      scope="colgroup"
                      colSpan={group.columns.length}
                      className={`${TABLE_HEAD} border-l border-neutral-200 text-center dark:border-white/12`}
                    >
                      {group.label}
                    </th>
                  ) : (
                    // 상위 헤더가 없는 열은 두 줄을 세로로 걸친다(원본의 rowspan).
                    group.columns.map((column) => (
                      <th
                        key={column.key}
                        scope="col"
                        rowSpan={2}
                        className={TABLE_HEAD}
                      >
                        {column.label}
                      </th>
                    ))
                  ),
                )}
                {!hideChanges && <th className="w-20 px-3.5" rowSpan={2} />}
              </tr>
            )}
            <tr className="border-b border-neutral-200 dark:border-white/12">
              {!hasGroupRow && (
                <th scope="col" className={`${TABLE_HEAD} text-left`}>
                  {axisHeader}
                </th>
              )}
              {table.columns.map((column) =>
                // 그룹 행이 있으면 그룹 없는 열은 위에서 이미 rowSpan 으로 그렸다.
                hasGroupRow && !column.group?.trim() ? null : (
                  <th key={column.key} scope="col" className={TABLE_HEAD}>
                    {column.label}
                  </th>
                ),
              )}
              {!hideChanges && !hasGroupRow && (
                <th scope="col" className="w-20 px-3.5" />
              )}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, rowIndex) => (
              <tr
                key={row.key}
                className="border-b border-neutral-100 last:border-b-0 dark:border-white/8"
              >
                {/* 세로 병합 — 시작 칸만 rowSpan 으로 그리고 덮인 칸은 건너뛴다.
                    값은 덮인 행에도 들어 있어서, 병합을 몰라도 데이터는 온전하다. */}
                {layout[rowIndex][LABEL_CELL] > 0 && (
                  <th
                    scope="row"
                    rowSpan={layout[rowIndex][LABEL_CELL]}
                    className={`${TABLE_ROW_LABEL} ${layout[rowIndex][LABEL_CELL] > 1 ? SPANNED_CELL : ''}`}
                  >
                    <span className="text-neutral-900 dark:text-neutral-100">
                      {row.label}
                    </span>
                  </th>
                )}
                {table.columns.map((column) => {
                  const size = layout[rowIndex][column.key];
                  if (size === 0) return null;
                  const changed =
                    !hideChanges && row.changedColumns.includes(column.key);
                  return (
                    <td
                      key={column.key}
                      rowSpan={size}
                      className={`${TABLE_CELL} ${size > 1 ? SPANNED_CELL : ''} ${changed ? CHANGED_CELL : ''}`}
                    >
                      {/* 빈 칸은 원본에도 흔하다. 공백으로 두면 "0" 으로 읽힌다. */}
                      {row.values[column.key] ?? '-'}
                    </td>
                  );
                })}
                {!hideChanges && (
                  <td className="px-3.5 py-2.5 text-right">
                    <PumsemChangeBadge
                      change={row.change}
                      editionLabel={editionLabel}
                    />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
}
